import _extends from "@babel/runtime/helpers/extends";
import { mediaAlternativesRule, RuleId } from '@wix/a11y-rules';
import { DEFAULT_BRANCH_ID } from '@wix/a11y-wizard-common';
import { Box, Divider } from '@wix/design-system';
import { useErrorHandler, useTranslation } from '@wix/fe-essentials/react';
import { convertElementStatusToBooleanStatus, ElementStatus, RuleElementListHeader, useAutoScrolling, useDeepCompare, useElementToggle } from '@wix/rules-panel-ui';
import { produce } from 'immer';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ruleApplyTriggeredAtom, scrollerNodeRefAtom } from '../../../atoms/session-atoms';
import { BiCtaNames } from '../../../bi/types';
import { usePanelStateAtom } from '../../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../hooks/use-bi-logger';
import { useSessionInfoContext } from '../../../hooks/use-session-info-context';
import { PanoramaErrorNames, PanoramaTransactions } from '../../../panorama/constants';
import { usePanoramaFunctions } from '../../../panorama/use-panorama-functions';
import { getUpdatedMediaAlternativesFromState } from '../../../rule-mappers/rule-handlers/media-alternatives/get-updated-media-alternatives-from-state';
import { useGlobalSectionState } from '../../../rule-mappers/shared/use-global-section-state';
import { useRuleBase } from '../../../rule-mappers/shared/use-rule-base';
import { showSaveErrorModal } from '../../modals/show-save-error-modal';
import { convertScanStatusToUiStatus } from '../alt-image-fixer/utils';
import { MediaAlternativesRule } from './media-alternatives-rule';
export const MediaAlternativesRuleFixer = _ref => {
  var _ruleState$state2, _ruleState$state4;
  let {
    pageId,
    getEntityId,
    mediaRules,
    openMediaElement,
    appDefinitionId,
    setMediaAlternatives,
    disabled = false,
    status
  } = _ref;
  const biLogger = useBiLogger();
  const {
    t
  } = useTranslation();
  const {
    getResolvedError
  } = useErrorHandler();
  const ruleChecker = mediaAlternativesRule.checker;
  const {
    finishPanoramaTransaction,
    startPanoramaTransaction,
    reportErrorPanorama
  } = usePanoramaFunctions();
  const {
    getRuleState,
    panelState
  } = usePanelStateAtom();
  const {
    updateRuleStateAndVerifyIfNeeded,
    setIsApplyTriggered
  } = useRuleBase();
  const setRuleApplyTriggered = useSetAtom(ruleApplyTriggeredAtom);
  const {
    updateGlobalSectionStateIfNeeded,
    getGlobalSectionModifiedComponentIds
  } = useGlobalSectionState();
  const {
    branchId: rawBranchId
  } = useSessionInfoContext();
  const branchId = rawBranchId || DEFAULT_BRANCH_ID;
  const {
    a11yWizardAdapter,
    capabilities,
    editorHost
  } = useA11yWizardAdapter();
  const compactPanelChrome = capabilities.compactPanelChrome;
  const {
    collapseElement,
    openNextElement,
    toggleElement,
    selectedElementId,
    previousOpenElementId
  } = useElementToggle();
  const [showLoader, setShowLoader] = useState(false);
  const ruleState = getRuleState(pageId, RuleId.MEDIA_ALTERNATIVES);
  const mediaPropsWithState = useMemo(() => {
    var _ruleState$state;
    return getUpdatedMediaAlternativesFromState(mediaRules, ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.savedMedia, panelState.globalSectionComponentsDataMap);
  }, [mediaRules, panelState.globalSectionComponentsDataMap, ruleState == null || (_ruleState$state2 = ruleState.state) == null ? void 0 : _ruleState$state2.savedMedia]);
  const [mediaElements, setMediaElements] = useState(mediaPropsWithState);
  const memoizedElements = useDeepCompare(mediaPropsWithState);
  const completedSize = useMemo(() => memoizedElements.filter(rule => convertElementStatusToBooleanStatus(rule.status)).length, [memoizedElements]);
  const total = useMemo(() => memoizedElements.length, [memoizedElements]);
  const scrollerNodeRef = useAtomValue(scrollerNodeRefAtom);
  const {
    selectedItemContentRef,
    selectedItemHeaderRef
  } = useAutoScrolling(selectedElementId, scrollerNodeRef);
  const handleCollapseAnimationEnd = useCallback(() => {
    setShowLoader(false);
    setMediaElements(memoizedElements);
  }, [memoizedElements]);
  useEffect(() => {
    openNextElement(memoizedElements, openMediaElement, handleCollapseAnimationEnd);
  }, [memoizedElements, handleCollapseAnimationEnd, openNextElement, openMediaElement]);
  const handleClickCancel = useCallback(index => () => {
    biLogger.taskElementCtaClick({
      ctaName: BiCtaNames.Cancel,
      index,
      status: mediaPropsWithState[index].status
    });
    collapseElement();
  }, [biLogger, collapseElement, mediaPropsWithState]);
  const handleClickApply = useCallback(index => async type => {
    const {
      id,
      tpaData,
      breakpointId,
      entityId
    } = mediaPropsWithState[index];
    startPanoramaTransaction(PanoramaTransactions.mediaAlternativesOnApply, {
      breakpointId,
      id,
      type
    });
    biLogger.taskElementCtaClick({
      ctaName: BiCtaNames.Apply,
      index,
      status: mediaPropsWithState[index].status
    });
    biLogger.taskElementApplyClick({
      index,
      status: mediaPropsWithState[index].status,
      value: type
    });
    setShowLoader(true);
    try {
      var _ruleState$state3;
      await setMediaAlternatives({
        branchId,
        breakpointId,
        id,
        pageId: entityId,
        tpaData,
        type
      });
      const stateMap = (ruleState == null || (_ruleState$state3 = ruleState.state) == null ? void 0 : _ruleState$state3.savedMedia) ?? new Map();
      const {
        status: statusAfterApply
      } = ruleChecker({
        allMedia: mediaPropsWithState.map(media => media.id === id ? {
          ...media,
          type
        } : media)
      });
      const updatedStateMap = produce(stateMap, draft => {
        var _draft$get;
        return draft.set(id, {
          ...draft.get(id),
          entityId,
          id,
          status: ElementStatus.Passed,
          title: ((_draft$get = draft.get(id)) == null ? void 0 : _draft$get.title) ?? '',
          type
        });
      });
      const updatedGlobalSectionDataMap = updateGlobalSectionStateIfNeeded(new Map([[id, {
        componentId: entityId,
        mediaType: type
      }]]));
      updateRuleStateAndVerifyIfNeeded(pageId, RuleId.MEDIA_ALTERNATIVES, {
        savedMedia: updatedStateMap,
        status: convertScanStatusToUiStatus(statusAfterApply)
      }, getGlobalSectionModifiedComponentIds(updatedGlobalSectionDataMap));
    } catch (error) {
      reportErrorPanorama(error, PanoramaErrorNames.updateMediaAlternatives);
      console.error(`Failed to update media alternatives alt "${type}" for component ${id}`, error);
      setShowLoader(false);
      await showSaveErrorModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        errorMessage: getResolvedError(error).message,
        handleTryAgain: () => {
          handleClickApply(index)(type);
        },
        t
      });
      return;
    }
    setIsApplyTriggered(true);
    setRuleApplyTriggered({
      pageId,
      ruleId: RuleId.MEDIA_ALTERNATIVES
    });
    finishPanoramaTransaction(PanoramaTransactions.mediaAlternativesOnApply, {
      breakpointId,
      id,
      type
    });
  }, [mediaPropsWithState, startPanoramaTransaction, biLogger, setIsApplyTriggered, setRuleApplyTriggered, finishPanoramaTransaction, setMediaAlternatives, branchId, ruleState == null || (_ruleState$state4 = ruleState.state) == null ? void 0 : _ruleState$state4.savedMedia, ruleChecker, updateGlobalSectionStateIfNeeded, updateRuleStateAndVerifyIfNeeded, pageId, getGlobalSectionModifiedComponentIds, reportErrorPanorama, a11yWizardAdapter, editorHost, getResolvedError, t]);
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: compactPanelChrome ? 'SP2' : 'SP1'
  }, !compactPanelChrome && /*#__PURE__*/React.createElement(Box, {
    padding: "SP1 0"
  }, /*#__PURE__*/React.createElement(Divider, null)), status === 'passed' && compactPanelChrome && /*#__PURE__*/React.createElement(Box, {
    padding: "SP1 0"
  }, /*#__PURE__*/React.createElement(Divider, null)), /*#__PURE__*/React.createElement(RuleElementListHeader, {
    completedSize: completedSize,
    light: compactPanelChrome,
    title: t('accessibility-wizard.rules-list.media-alternatives.list.failed-elements-number.heading'),
    totalSize: total
  }), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: compactPanelChrome ? 'SP2' : 'SP1'
  }, mediaElements.map((ruleProps, index) => {
    return /*#__PURE__*/React.createElement(MediaAlternativesRule, _extends({
      key: ruleProps.id,
      onClickApply: handleClickApply(index),
      onClickCancel: handleClickCancel(index)
    }, ruleProps, {
      appDefinitionId: appDefinitionId,
      contentRef: selectedItemContentRef,
      disabled: disabled,
      elementIndex: index,
      getEntityId: getEntityId,
      headerRef: selectedItemHeaderRef,
      isOpen: !disabled && selectedElementId === ruleProps.id,
      pageId: pageId,
      previousOpenElementId: previousOpenElementId,
      showLoader: showLoader,
      toggleElement: toggleElement(ruleProps.id, () => openMediaElement(ruleProps))
    }));
  })));
};
//# sourceMappingURL=media-alternatives-rule-fixer.js.map